/**

 /\                 /\
/ \'._   (\_/)   _.'/ \
|.''._'--(o.o)--'_.''.|
 \_ / `;=/ " \=;` \ _/
   `\__| \___/ |__/`
        \(_|_)/
         " ` "

I came, I saw, I conquered

 */

/*

==================================================================================================

CUSTOM CLASSES / WRAPPERS

==================================================================================================


/**
 * A wrapper for a dynamic fully loaded async cart
 *
 * @class cartFlyout
 * @typedef {CartFlyout}
 * @extends {HTMLElement}
 */

let isCheckingGWP = false;

class CartFlyout extends HTMLElement {
  constructor() {
    super();
    // bind the escape key to always close the cart
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') {
        if (this.classList.contains('open')) {
          this.close();
        } else {
          return false;
        }
      }
    });

    // used for cart abandon
    const urlParams = new URLSearchParams(window.location.search);
    const shouldCartOpenFromParams = urlParams.get('bag-open');
    // console.log(shouldCartOpenFromParams);
    if (shouldCartOpenFromParams) {
      // this.open();
      this.setAttribute('data-open', true);
      this.classList.add('open');
      document.querySelector('body').classList.add('body-locked');
      document.querySelector('cart-overlay').classList.add('open');
    }
  }

  render() {
    this.setAttribute('cart-ref', '');
  }

  connectedCallback() {
    this.render();

    checkGWP();
  }

  // if the theme settings has the cart flyout enabled, oprn it
  // if not, then redirect to cart page
  open() {
    this.setAttribute('data-open', true);
    let isEnabled = this.getAttribute('data-enabled');
    if (!isEnabled || isEnabled == 'false') {
      window.location.href = window?.Shopify?.routes?.root + 'cart';
      return false;
    } else if (window.location.pathname == '/cart') {
      return false;
    } else {
      this.classList.add('open');
      document.querySelector('body').classList.add('body-locked');
      document.querySelector('cart-overlay').classList.add('open');

      checkGWP();
    }
  }

  close() {
    this.setAttribute('data-open', false);
    this.classList.remove('open');
    document.querySelector('body').classList.remove('body-locked');
    document.querySelector('cart-overlay').classList.remove('open');
  }

  /**
   * Helper for quickly returning the amounf of items in cart
   *
   * @async
   * @returns {Number} - cart count
   */
  async getCount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const res = await response.json();
      return res?.item_count;
    } catch (error) {
      console.error('Error:', error);
    }
  }
}

customElements.define('cart-flyout', CartFlyout);

// Create a new cart instance and call the methods from outside their classes
const _default = document.querySelector(`[cart-ref]`);

class CartCounter extends HTMLElement {
  constructor() {
    super();
  }

  // watch for changes in the selected checkout option
  static get observedAttributes() {
    return ['data-in-sync'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue != null) {
      this.resync();
    }
  }

  async render() {
    let c = await _default.getCount();
    this.setAttribute('counter-ref', '');
    this.setAttribute('data-count', c);
  }

  async resync() {
    let c = await _default.getCount();
    this.setAttribute('data-count', c);
    this.innerHTML = `${c}`;
  }

  connectedCallback() {
    this.render();
  }
}

customElements.define('cart-count', CartCounter);

let _counter = document.querySelector(`cart-count`);

/**
 * Quantity adjusters for items in the cart
 *
 * @class cartFlyoutQuantity
 * @typedef {CartFlyoutQuantity}
 * @extends {CartFlyout}
 */
class CartFlyoutQuantity extends CartFlyout {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      let _type = e.currentTarget.getAttribute('data-type');
      let _amount = parseInt(e.currentTarget.getAttribute('data-current'));
      this.adjustQuantity(_type, _amount);
    });
  }

  adjustQuantity(t, a) {
    _counter.setAttribute('data-in-sync', false);
    if (t == 'up') {
      a = a + 1;
      this.hydrateCartItem(a);
    } else {
      a = a - 1;
      this.hydrateCartItem(a);
    }
  }

  async hydrateCartItem(a) {
    let k = this.getAttribute('data-key');

    let updates = {
      [k]: a,
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      const res = await response.json();
      resyncCart();
    } catch (error) {
      console.error('Error:', error);
    }
  }
}

customElements.define('cart-flyout-quantity', CartFlyoutQuantity);

/**
 * A wrapepr for removing all quantities of a given line item
 *
 * @class cartItemRemove
 * @typedef {CartItemRemove}
 * @extends {CartFlyout}
 */
class CartItemRemove extends CartFlyout {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      let _key = e.currentTarget.getAttribute('data-key');
      this.kill(_key);
    });
  }

  /**
   * Remove the given line item from the attached cart
   *
   * @async
   * @param {*} _id
   * @returns {object} Cart object
   */
  async kill(_id) {
    let updates = {
      [_id]: 0,
    };

    _counter.setAttribute('data-in-sync', false);

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      const res = await response.json();
      resyncCart();
      return res;
    } catch (error) {
      console.error('Error:', error);
    }
  }
}

customElements.define('cart-flyout-remove', CartItemRemove);

/**
 * Button for easily adding items to the cart - 1 quantity at a time
 *
 * @class AddToCart
 * @typedef {AddToCart}
 * @extends {HTMLElement}
 */
class AddToCart extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', () => {
      this.addOnce();
    });
  }

  async addOnce() {
    document.querySelector('cart-flyout').classList.add('waiting');
    document.querySelector('#awaiting').classList.add('waiting');

    const props = this.querySelectorAll('[js-props]');
    let _props = [];
    if (props.length > 0) {
      props.forEach((el) => {
        let _key = el.getAttribute('name');
        let _val = el.value;
        _props.push({ [_key]: _val });
      });
    }
    _props = Object.assign({}, ..._props);

    _counter.setAttribute('data-in-sync', false);

    let i = this.getAttribute('data-id');
    let q = this.getAttribute('data-quantity');
    // if props aren't empty, add them to the cart

    let formData = {
      items: [
        {
          id: i,
          quantity: parseInt(q) || 1,
          properties: _props,
        },
      ],
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const res = await response.json();
      resyncCart();
    } catch (error) {
      console.error('Error:', error);
    }

    document.querySelector('cart-flyout').classList.remove('waiting');

    _default.open();
  }
}

customElements.define('add-to-cart', AddToCart);

class ProductQuantity extends CartFlyout {
  constructor() {
    super();
    this.addEventListener('click', (e) => {
      e.preventDefault();
      this.determineAmounts();
    });
  }

  determineAmounts() {
    let _id = this.getAttribute('data-id');
    let _type = this.getAttribute('data-type');
    let _q = parseInt(document.querySelector(`#dq-${_id}`).getAttribute('data-current'));

    if (_type == 'up') {
      _q = _q + 1;
      document.querySelector(`#dq-${_id}`).setAttribute('data-current', _q);
      document.querySelector(`#atc-${_id}`).setAttribute('data-quantity', _q);
      document.querySelector(`#dq-${_id}`).innerHTML = _q;
    } else {
      if (_q == 1) {
        return false;
      } else {
        _q = _q - 1;
        document.querySelector(`#dq-${_id}`).setAttribute('data-current', _q);
        document.querySelector(`#atc-${_id}`).setAttribute('data-quantity', _q);
        document.querySelector(`#dq-${_id}`).innerHTML = _q;
      }
    }
  }
}

customElements.define('product-quantity', ProductQuantity);

/**
 * Cart bg overlay with event handler
 *
 * @class CartOverlay
 * @typedef {CartOverlay}
 * @extends {HTMLElement}
 */
class CartOverlay extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (e) => {
      _default.close();
    });
  }
}

customElements.define('cart-overlay', CartOverlay);

/*

==================================================================================================

UTILITIES

==================================================================================================


*/

/**
 * A useful way for detetcing when elements are added to the DOM via async code
 *
 * @param {string} selector - any valid query selector
 * @returns {*}
 */
const waitForElm = (selector) => {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
};


async function checkGWP() {
  if (isCheckingGWP) {
    return false;
  }
  isCheckingGWP = true;

  const cartFlyout = document.querySelector('cart-flyout');

  if (!cartFlyout) {
    isCheckingGWP = false;

    return false;
  }

  const enabled = cartFlyout.getAttribute('data-gwp-enabled') === 'true';
  const variantId = cartFlyout.getAttribute('data-gwp-variant-id');
  const threshold = parseFloat(cartFlyout.getAttribute('data-gwp-threshold')) * 100;

  if (!enabled || !variantId) {
    isCheckingGWP = false;

    return false;
  }

  try {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');
    const cart = await response.json();

    const gwpAutoItem = cart.items.find((item) => {
      const isMatch = item.variant_id.toString() === variantId.toString() || item.id.toString() === variantId.toString();
      return isMatch && item.properties && item.properties._gwp_auto === 'true';
    });

    const gwpManualItem = cart.items.find((item) => {
      const isMatch = item.variant_id.toString() === variantId.toString() || item.id.toString() === variantId.toString();
      return isMatch && (!item.properties || item.properties._gwp_auto !== 'true');
    });

    let gwpPrice = 0;
    cart.items.forEach((item) => {
      const isMatch = item.variant_id.toString() === variantId.toString() || item.id.toString() === variantId.toString();
      if (isMatch) {
        gwpPrice += item.final_line_price;
      }
    });

    const qualifyingSubtotal = cart.total_price - gwpPrice;

    if (qualifyingSubtotal >= threshold) {
      if (gwpManualItem) {
        if (gwpAutoItem) {
          const updates = {
            [gwpAutoItem.key]: 0,
          };

          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          });

          await resyncCart(true);

          return true;
        } else if (gwpManualItem.quantity !== 1) {
          const updates = {
            [gwpManualItem.key]: 1,
          };

          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          });

          await resyncCart(true);

          return true;
        }
      } else {
        if (!gwpAutoItem) {
          await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: {
                    _gwp_auto: 'true',
                  },
                },
              ],
            }),
          });

          await resyncCart(true);

          return true;
        } else if (gwpAutoItem.quantity !== 1) {
          const updates = {
            [gwpAutoItem.key]: 1,
          };

          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates }),
          });

          await resyncCart(true);

          return true;
        }
      }
    } else {
      if (gwpAutoItem) {
        const updates = {
          [gwpAutoItem.key]: 0,
        };

        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });

        await resyncCart(true);

        return true;
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    isCheckingGWP = false;
  }

  return false;
}

/**
 * Update the cart and re sync with the liquid template in 1 action 🎩
 *
 * @async
 * @param {*} _id
 * @returns {object} Cart object
 */
async function resyncCart(skipGwpCheck = false) {
  if (!skipGwpCheck) {
    const mutated = await checkGWP();

    if (mutated) {
      return;
    }
  }

  document.querySelector('cart-flyout').classList.add('waiting');
  document.querySelector('#awaiting').classList.add('waiting');

  // the amount of skeleton items to show
  let _spooky = await _default.getCount();
  let _original = document.querySelector('.spooky-skeleton');

  if (_original) {
    for (let i = 1; i < _spooky; i++) {
      let _new = _original.cloneNode(true);
      document.querySelector('#awaiting').appendChild(_new);
    }
  }

  try {
    const response = await fetch(window.Shopify.routes.root + 'cart?view=sync');
    const res = await response.text();

    let p = new DOMParser();
    let doc = p.parseFromString(res, 'text/html');
    let template = doc.querySelector('#main-cart #aside-cart-contents');
    let original = document.querySelector('#main-cart');
    let prev = document.querySelector('#main-cart #aside-cart-contents');

    doc.querySelectorAll('[data-cart-close="true"]').forEach((el) => {
      el.addEventListener('click', () => {
        _default.close();
      });
    });

    original.replaceChild(template, prev);
    _counter.setAttribute('data-in-sync', true);
  } catch (error) {
    console.error('Error:', error);
  }

  document.querySelector('cart-flyout').classList.remove('waiting');
};

// Make resyncCart available globally
window.resyncCart = resyncCart;

// Open the cart any time a trigger is clicked
document.querySelectorAll('[data-cart-trigger="true"]').forEach((el) => {
  el.addEventListener('click', () => {
    _default.open();
  });
});

// Close the cart any time a trigger is clicked
document.querySelectorAll('[data-cart-close="true"]').forEach((el) => {
  el.addEventListener('click', () => {
    _default.close();
  });
});
